import { useEffect, useState } from "react";
import { Alert, Button, Empty, Input, Table, Tooltip, Typography, message } from "antd";
import type { StockBatchStock, StockSkuStock } from "@shoe-trace/shared";
import { downloadFile, fetchStockBySku } from "../api/client";
import { LoadFail, PageSpin } from "../components/widgets";

const UNSAFE_FILENAME = /[\\/:*?"<>|\u0000-\u001f]/g;

function stockExportFilename(rawQuery: string): string {
  const query = rawQuery.trim().replace(UNSAFE_FILENAME, "").trim().slice(0, 80);
  if (!query) return "库存.xlsx";
  return `库存-${query}.xlsx`;
}

function stockExportPath(rawQuery: string): string {
  const params = new URLSearchParams();
  const query = rawQuery.trim();
  if (query) params.set("q", query);
  const text = params.toString();
  return `/api/stock/skus.xlsx${text ? `?${text}` : ""}`;
}

const EMPTY_STOCK = "还没有待入库、待上架或可用的鞋。到货导入并扫码入库后，这里按 SKU 显示双数。";

const PICKABLE_HINT = "已经扫过、已经放到库位、还没装进出库单。这些双现在可以拿去拣。";
const UNLOCATED_HINT = "已经扫过入库，库位还空着。";
const UNPOSTED_HINT = "吊牌已经打出，还没扫。扫过才算入库。";
const PICKABLE_QTY_HINT = "这一批里已经上架、现在可以拿去拣的双数。";
const UNLOCATED_QTY_HINT = "这一批里已经入库、还没放到库位的双数。";

function hintTitle(label: string, hint: string): JSX.Element {
  return (
    <Tooltip title={hint}>
      <span>{label}</span>
    </Tooltip>
  );
}

function locationText(code: string | null): string {
  return code ?? "待上架";
}

function batchTable(row: StockSkuStock): JSX.Element {
  if (row.batches.length === 0) {
    return <Typography.Text type="secondary">这个 SKU 还没有已入库的批次。</Typography.Text>;
  }
  return (
    <Table<StockBatchStock>
      size="small"
      pagination={false}
      rowKey={(batch) => `${batch.batchNo}:${batch.locationCode ?? ""}`}
      dataSource={row.batches}
      scroll={{ x: 560 }}
      columns={[
        { title: "批次号", dataIndex: "batchNo", width: 180 },
        {
          title: "库位",
          dataIndex: "locationCode",
          width: 140,
          render: (value: string | null) => locationText(value),
        },
        { title: hintTitle("可用双数", PICKABLE_QTY_HINT), dataIndex: "pickableQty", width: 120, align: "right" },
        { title: hintTitle("待上架双数", UNLOCATED_QTY_HINT), dataIndex: "unlocatedQty", width: 120, align: "right" },
      ]}
    />
  );
}

export function StockBySku(): JSX.Element {
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<StockSkuStock[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    void fetchStockBySku(query)
      .then((result) => {
        if (cancelled) return;
        setRows(result.rows);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, tick]);

  if (loading && rows === null) return <PageSpin tip="正在按 SKU 汇总库存…" />;
  if (failed || rows === null) {
    return <LoadFail title="库存没有读出来。" onRetry={() => setTick((current) => current + 1)} />;
  }

  const searched = query.length > 0;

  async function exportStock(): Promise<void> {
    setExporting(true);
    try {
      await downloadFile(stockExportPath(query), stockExportFilename(query));
      message.success("库存表已下载。数字和你正在看的这一页一样。");
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "这一步没有完成。请稍后再试一次。");
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        库存
      </Typography.Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="一行一个 SKU。可用：已经扫过、已经放到库位、还没装进出库单，这些双现在可以拿去拣。待上架：已经扫过入库，库位还空着。待入库：吊牌已经打出，还没扫，扫过才算入库。点一行看这一款各批次的库位。点「导出库存」下载你正在看的这些数字，Excel 里有按 SKU 和按批次库位两张表。"
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 16 }}>
        <Input.Search
          allowClear
          value={draft}
          placeholder="输入 SKU 的一部分，包括双数都是 0 的档案"
          enterButton="查找这个 SKU"
          style={{ maxWidth: 520, flex: "1 1 280px" }}
          onChange={(event) => setDraft(event.target.value)}
          onSearch={(value, event, info) => {
            void event;
            const next = info?.source === "clear" ? "" : value.trim();
            setDraft(next);
            setQuery(next);
          }}
        />
        <Button disabled={rows.length < 1 || loading} loading={exporting} onClick={() => void exportStock()}>
          导出库存
        </Button>
      </div>
      <Table<StockSkuStock>
        rowKey="humanCode"
        loading={loading}
        dataSource={rows}
        scroll={{ x: 760 }}
        pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false, showTotal: (total) => `共 ${total} 个 SKU` }}
        locale={{
          emptyText: (
            <Empty
              description={
                searched ? "没有包含这段文字的 SKU。核对完整规格后再查一次。" : EMPTY_STOCK
              }
            />
          ),
        }}
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (row) => batchTable(row),
        }}
        onRow={() => ({ style: { cursor: "pointer" } })}
        columns={[
          { title: "SKU", dataIndex: "humanCode", width: 220 },
          { title: "款式名称", dataIndex: "name", width: 220 },
          { title: hintTitle("可用", PICKABLE_HINT), dataIndex: "pickableQty", width: 100, align: "right" },
          { title: hintTitle("待上架", UNLOCATED_HINT), dataIndex: "unlocatedQty", width: 100, align: "right" },
          { title: hintTitle("待入库", UNPOSTED_HINT), dataIndex: "unpostedQty", width: 100, align: "right" },
        ]}
      />
    </>
  );
}
